import type { ReactNode } from "react";
import {
  Award,
  Briefcase,
  FileText,
  GraduationCap,
  Sparkles,
} from "lucide-react";
import type { ApplicationFormData } from "./types";

interface ReviewStepProps {
  formData: ApplicationFormData;
  emailValue: string;
  skills: string[];
  workCount: number;
  educationCount: number;
  certCount: number;
  resumeName?: string;
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <p
        className="text-[10px] font-bold uppercase tracking-widest"
        style={{ color: "var(--landing-text-dim)" }}
      >
        {label}
      </p>
      {/* A long email breaks instead of widening its column past the card. */}
      <p className="text-sm font-semibold [overflow-wrap:anywhere]" style={{ color: "var(--landing-text)" }}>
        {value || "—"}
      </p>
    </div>
  );
}

function StatChip({
  icon: Icon,
  count,
  label,
}: {
  icon: typeof Briefcase;
  count: number;
  label: string;
}) {
  return (
    <div
      className="flex items-center gap-2.5 rounded-xl border px-3.5 py-3"
      style={{
        background: "var(--landing-surface)",
        borderColor: "var(--landing-glass-border)",
      }}
    >
      <Icon className="h-4 w-4 shrink-0" style={{ color: "rgba(var(--acc2-rgb),0.7)" }} />
      <div>
        <p className="text-base font-bold leading-none tabular-nums" style={{ color: "var(--landing-text)" }}>
          {count}
        </p>
        <p className="mt-0.5 text-[11px]" style={{ color: "var(--landing-text-muted)" }}>
          {label}
        </p>
      </div>
    </div>
  );
}

export function ReviewStep({
  formData,
  emailValue,
  skills,
  workCount,
  educationCount,
  certCount,
  resumeName,
}: ReviewStepProps) {
  const phone = formData.phone
    ? `${formData.phoneCountryCode.replace(/^\+1-(US|CA)$/, "+1")} ${formData.phone}`
    : "—";

  return (
    <div className="space-y-6">
      <div
        className="flex items-start gap-3 rounded-xl p-4"
        style={{
          background:
            "linear-gradient(120deg, rgba(var(--pos-rgb),0.07), rgba(var(--acc2-rgb),0.05))",
          border: "1px solid rgba(var(--acc2-rgb),0.2)",
        }}
      >
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[var(--pos)]" />
        <p className="text-sm leading-relaxed" style={{ color: "var(--landing-text-secondary)" }}>
          Everything looks ready. Give it a final scan. You can jump back to any
          step from the timeline to make edits before submitting.
        </p>
      </div>

      {/* Identity */}
      <div
        className="rounded-2xl border p-5"
        style={{ background: "var(--landing-surface)", borderColor: "var(--landing-glass-border)" }}
      >
        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
          <Row label="Name" value={`${formData.firstName} ${formData.lastName}`.trim()} />
          <Row label="Email" value={emailValue} />
          <Row label="Phone" value={phone} />
          <Row label="Country" value={formData.country} />
          {formData.headline && <Row label="Headline" value={formData.headline} />}
          {formData.expectedSalary && (
            <Row
              label="Expected salary"
              value={`${formData.expectedSalary} ${formData.salaryCurrency}`}
            />
          )}
        </div>

        {formData.professionalSummary && (
          <div className="mt-4 border-t pt-4" style={{ borderColor: "var(--landing-glass-border)" }}>
            <p
              className="text-[10px] font-bold uppercase tracking-widest"
              style={{ color: "var(--landing-text-dim)" }}
            >
              Summary
            </p>
            <p className="mt-1 text-sm leading-relaxed" style={{ color: "var(--landing-text-secondary)" }}>
              {formData.professionalSummary.length > 220
                ? `${formData.professionalSummary.slice(0, 220)}…`
                : formData.professionalSummary}
            </p>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatChip icon={Briefcase} count={workCount} label="Experience" />
        <StatChip icon={GraduationCap} count={educationCount} label="Education" />
        <StatChip icon={Sparkles} count={skills.length} label="Skills" />
        <StatChip icon={Award} count={certCount} label="Certifications" />
      </div>

      {resumeName && (
        <div
          className="flex items-center gap-2.5 rounded-xl border p-3.5"
          style={{ background: "var(--landing-surface)", borderColor: "var(--landing-glass-border)" }}
        >
          <FileText className="h-4 w-4 shrink-0" style={{ color: "var(--landing-text-muted)" }} />
          <span className="truncate text-sm font-medium" style={{ color: "var(--landing-text)" }}>
            {resumeName}
          </span>
        </div>
      )}

      {skills.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {skills.map((s, i) => (
            <span
              key={`${s}-${i}`}
              className="inline-flex rounded-full px-2.5 py-1 text-xs font-medium"
              style={{
                background: "rgba(var(--acc2-rgb),0.08)",
                color: "#00B0D8",
                border: "1px solid rgba(var(--acc2-rgb),0.2)",
              }}
            >
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
